import { useState } from 'react'
import { PrismBanner, PrismButton, PrismDialog } from '@prism-ui/react'
import useFetch from '../../../../hooks/useFetch'
import axios from 'axios'
import AddUrlModal from './AddUrlModal'
import './EmailReminders.css'

interface EmailUrl {
  id: number
  name: string
  url: string
}

interface ManageUrlsProps {
  setSuccessMessage: (message: string) => void
  setErrorMessage: (message: string) => void
  onUrlsChanged: () => void
}

const ManageUrls = ({ setSuccessMessage, setErrorMessage: _setErrorMessage, onUrlsChanged }: ManageUrlsProps) => {
  const { data: urls = [], refresh } = useFetch<EmailUrl[]>('/api/email_urls')
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number>()
  const [editName, setEditName] = useState('')
  const [editUrl, setEditUrl] = useState('')
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [urlToDelete, setUrlToDelete] = useState<EmailUrl | null>(null)
  const [deleteError, setDeleteError] = useState('')

  const openModal = (urlRecord?: EmailUrl) => {
    if (urlRecord) {
      setEditId(urlRecord.id)
      setEditName(urlRecord.name)
      setEditUrl(urlRecord.url)
    } else {
      setEditId(undefined)
      setEditName('')
      setEditUrl('')
    }
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditId(undefined)
    setEditName('')
    setEditUrl('')
  }

  const handleUrlCreated = (urlRecord: EmailUrl) => {
    const action = editId ? 'updated' : 'created'
    setSuccessMessage(`URL "${urlRecord.name}" ${action} successfully!`)
    refresh()
    onUrlsChanged()
  }

  const handleDeleteClick = (urlRecord: EmailUrl) => {
    setUrlToDelete(urlRecord)
    setDeleteError('')
    setShowDeleteModal(true)
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setUrlToDelete(null)
    setDeleteError('')
  }

  const handleConfirmDelete = async () => {
    if (!urlToDelete) return
    
    try {
      await axios.delete(`/api/email_urls/${urlToDelete.id}`)
      setSuccessMessage(`URL "${urlToDelete.name}" deleted successfully!`)
      setShowDeleteModal(false)
      setUrlToDelete(null)
      setDeleteError('')
      refresh()
      onUrlsChanged()
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } }
      const error = axiosError.response?.data?.error || 'Failed to delete URL'
      setDeleteError(error)
    }
  }

  return (
    <div>
      <div className="email-urls-add-row">
        <PrismButton tone="primary" display="fill" onPrismClick={() => openModal()}>
          Add URL
        </PrismButton>
      </div>

      {urls.length === 0 ? (
        <p className="email-urls-empty">No URLs have been added yet.</p>
      ) : (
        <ul className="email-urls-list">
          {urls.map((urlRecord) => (
            <li key={urlRecord.id} className="email-urls-item">
              <div className="email-urls-item-detail">
                <strong>{urlRecord.name}</strong>
                <a href={urlRecord.url} target="_blank" rel="noopener noreferrer" className="email-urls-item-link">
                  {urlRecord.url}
                </a>
              </div>
              <div className="email-urls-item-actions">
                <PrismButton size="sm" tone="primary" display="outline" onPrismClick={() => openModal(urlRecord)}>
                  Edit
                </PrismButton>
                <PrismButton size="sm" tone="danger" display="outline" onPrismClick={() => handleDeleteClick(urlRecord)}>
                  Delete
                </PrismButton>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Delete Confirmation dialog */}
      <PrismDialog
        open={showDeleteModal}
        setAriaLabel="Confirm Delete"
        onPrismClose={handleCancelDelete}
      >
        <span slot="header">Confirm Delete</span>
        <div className="email-urls-delete-body">
          <p>Are you sure you want to delete this URL?</p>
          <p className="email-urls-delete-name">&quot;{urlToDelete?.name}&quot;</p>

          <PrismBanner tone="caution">
            This action cannot be undone. This URL will be permanently deleted.
          </PrismBanner>

          {deleteError ? <PrismBanner tone="danger">{deleteError}</PrismBanner> : null}
        </div>

        <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={handleCancelDelete}>
          Cancel
        </PrismButton>
        <PrismButton slot="footer" tone="danger" display="fill" onPrismClick={handleConfirmDelete}>
          Delete
        </PrismButton>
      </PrismDialog>

      <AddUrlModal
        show={showModal}
        onHide={closeModal}
        onUrlCreated={handleUrlCreated}
        editId={editId}
        editName={editName}
        editUrl={editUrl}
      />
    </div>
  )
}

export default ManageUrls
